import type { VisitService } from './VisitService'
import { type FirestoreService } from '../http/FirestoreService'
import { functions, model } from '../../db'
import { type Patient } from '../../model/patient'
import { httpsCallable } from 'firebase/functions'
import {
  invalidateReports,
  reportingCall,
  type VisitEnvelope,
  type VisitWriteInput,
  type BillingInput
} from '../reporting/ReportingService'
import { v4 } from 'uuid'

export class VisitServiceImpl implements VisitService {
  private readonly firestoreService: FirestoreService
  private static readonly collectionName = 'visits'
  private static readonly parentCollectionName = 'patients'

  constructor(firestoreService: FirestoreService) {
    this.firestoreService = firestoreService
  }

  async createVisit(
    patientId: string,
    data: VisitWriteInput
  ): Promise<VisitEnvelope> {
    const create = httpsCallable<
      {
        patientId: string
        visit: VisitWriteInput
        billing?: BillingInput
        operationId: string
      },
      VisitEnvelope
    >(functions, 'createVisit')
    const { billing, operationId = v4(), ...visit } = data
    const result = await create({
      patientId,
      visit,
      billing,
      operationId
    })
    invalidateReports()
    return result.data
  }

  async updateVisit(
    patientId: string,
    visitId: string,
    data: VisitWriteInput
  ): Promise<VisitEnvelope> {
    const result = await reportingCall<VisitEnvelope>('mutateVisit', {
      patientId,
      visitId,
      changes: data,
      expectedRevision: data.revision ?? 0,
      operationId: data.operationId ?? v4(),
      kind: 'update'
    })
    invalidateReports()
    return result
  }

  async getVisits(patientId: string): Promise<VisitEnvelope[]> {
    const patientRef = this.firestoreService.getDocumentRef(
      VisitServiceImpl.parentCollectionName,
      patientId
    )

    const visits = await this.firestoreService.getChildDocuments<any>(
      patientRef,
      VisitServiceImpl.collectionName
    )
    return visits.filter((visit) => visit.status !== 'void')
  }

  async deleteVisit(
    patientId: string,
    visitId: string,
    expectedRevision = 0,
    reason = ''
  ): Promise<void> {
    await reportingCall('mutateVisit', {
      patientId,
      visitId,
      expectedRevision,
      reason,
      operationId: v4(),
      kind: 'void'
    })
    invalidateReports()
  }

  async getAISummary(patient: Patient, visits: any): Promise<any> {
    // Vertex AI is intentionally unavailable in test and emulator modes. Keep
    // those workflows deterministic without exposing a production model or
    // credentials to local runs or CI.
    if (!model) {
      const visitCount = Array.isArray(visits) ? visits.length : 0
      const complaintValue = Array.isArray(visits)
        ? visits.find((visit) => visit.complaint)?.complaint
        : undefined
      const latestComplaint =
        typeof complaintValue === 'string' ? complaintValue : undefined
      return `**${patient.name}** has ${visitCount} recorded clinic visit${
        visitCount === 1 ? '' : 's'
      }.${latestComplaint ? ` Latest complaint: ${latestComplaint}.` : ''}`
    }

    const prompt = `
      You are a doctor assistant. You are given all visit data of a patient.
      Please summarize the visit data into a brief short summary. 
      Make sure to include the patient's name, age, gender.
      Highlight the most important information with bold text (do not overuse it), do not use headers.
      You don't need to include details from every visit, just the overall summary.
      Please be concise and to the point, do not include any medical advice, just the summary of investigations, diagnoses and treatments.
      The patient's name is ${patient.name}, birth date is ${
        patient.dateOfBirth
      }, gender is ${patient.sex}.
      The visit data is: ${JSON.stringify(visits)}
    `

    const result = await model.generateContent(prompt)
    const response = result.response
    const text = response.text()

    return text
  }
}
